import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  BarChart2,
  CalendarRange,
  Check,
  ChevronRight,
  Coins,
  // FileSpreadsheet, // TEMP-HIDDEN: CRM SPDI Backfill sidebar item (this version)
  // FileText, // commented-out Consent Notices sidebar item
  FileEdit,
  Grip,
  Layers,
  LayoutList,
  Loader2,
  Lock,
  PencilLine,
  Plus,
  FlaskConical,
  ScrollText,
  ShieldCheck,
  SlidersHorizontal,
  FileText,
  // Trophy, // TEMP-HIDDEN: CRM Goals sidebar item (this version)
  UserCheck,
  Users as UsersIcon,
  UserX,
  Building,
  Bell,
  BellRing,
  CalendarClock,
  FileSpreadsheet,
  Landmark,
  Receipt,
  Timer,
  type LucideIcon,
} from 'lucide-react';
import { Badge, HighlightText, InfiniteScrollSentinel } from '@clarix/ui-shared';
import { Popover, PopoverContent, PopoverTrigger, cn } from '@clarix/ui';
import { apiService, baseService } from '@clarix/api-client';
import { usePermissionsSlice } from '@clarix/rbac';
import {
  usePmsCategories,
  usePmsCompetencies,
  usePmsBands,
  usePmsForms,
  usePmsRuns,
  usePmsUserRoles,
  usePmsRoleTemplates,
  usePmsTeamEvaluations,
  type FormTemplate,
  type TeamEvaluationRow,
  formTitle,
  formCycleState,
  FORM_CYCLE_LABEL,
} from '@clarix/services-pms';
import { useWorkspaceMasters } from '@clarix/services-master';
import { useAuthStore } from '@clarix/auth-store';
import { DeclarativePanel } from './sidebar/engine/SidebarEngine';
import {
  getDeclarativePanel,
  SIDEBAR_PROVIDERS,
  SIDEBAR_SECTION_PROVIDERS,
} from './sidebar/registry';
import {
  FavouritesSection,
  HoverNavRow,
  PanelRowIcon,
  PanelSectionHeader,
  SectionLabel,
  SidebarSearchInput,
} from './sidebar/engine/primitives';
import { PanelFavouritesProvider } from './sidebar/engine/PanelFavouritesProvider';
import { PANEL_ROW_CLASS } from './sidebar/engine/rowStyles';
import {
  PAYROLL_RUN_STATUS_LABEL,
  buildPayHistory,
  formatPayDate,
  payHistoryPeriodLabel,
  periodWarningMessage,
  type PayHistoryEntry,
  type PayHistoryGroup,
  type PayHistoryRun,
  type PayHistorySchedule,
} from '@clarix/utils';

/**
 * The sidebar panels that are still hand-written React: PAYROLL and PMS.
 * Projects, workspace, support and CRM are all OBJECT-DRIVEN now, declared as
 * pure JSON in `sidebar/<app>Panels.ts` and rendered by the engine in
 * `sidebar/engine/`. `renderSidebarPanel` checks that registry first, so
 * migrating payroll or PMS means writing its config and deleting its components
 * from this file — no dispatch change.
 *
 * The row look itself is NOT redefined here: `HoverNavRow`, `PanelRowIcon`,
 * `SidebarSearchInput`, `SectionLabel` and `PanelSectionHeader` all come from the engine's
 * primitives, so these panels and the object-driven ones can't drift apart.
 */

interface PayrollNavLink {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Overview/parent links are prefixes of their siblings — match exactly so
   *  they don't stay highlighted on child routes. */
  exact?: boolean;
  /**
   * Shell-namespace slug (`payroll.*`) gating this link. Applied only once
   * the payroll remote has published its matrix (`loadedApps.payroll`) —
   * before that the link renders. Links without a slug always show.
   */
  permission?: string;
}

interface PayrollNavGroup {
  heading?: string;
  links: readonly PayrollNavLink[];
}

// No in-panel title: the panel header already names the panel, so one here printed it twice.
const PAYROLL_PANELS: Record<string, { groups: readonly PayrollNavGroup[] }> = {
  'payroll-settings': {
    groups: [
      {
        // Configuration — the org-level settings. Org profile
        // (name/logo/GSTIN/address) is workspace-owned (edited in Workspace →
        // Company Profile); payroll keeps Work locations (same name as
        // Workspace → Masters → Work locations, which owns them) plus Roles
        // (RBAC), which moved here off the main rail.
        heading: 'Organisation Settings',
        links: [
          {
            to: '/payroll/organization/settings/branches',
            label: 'Work locations',
            icon: Building,
            permission: 'payroll.branch.view',
          },
          {
            to: '/payroll/roles',
            label: 'Roles & permissions',
            icon: ShieldCheck,
            permission: 'payroll.roles.manage',
          },
          {
            // Bank & Disbursement — the company's disbursing bank accounts
            // (drives the run page's bank bulk-transfer file). Gated like the
            // statutory setup: org finance config.
            to: '/payroll/organization/settings/bank-disbursement',
            label: 'Bank & Disbursement',
            icon: Landmark,
            permission: 'payroll.bank.view',
          },
          {
            // Notifications master — org-wide switchboard for every payroll
            // notification type (bell + email), admin-only by default.
            to: '/payroll/organization/settings/notifications',
            label: 'Notifications',
            icon: Bell,
            permission: 'payroll.notifications.manage',
          },
          {
            // Run window — opt-in to run past & future payroll (out-of-cycle),
            // not just the current month. Gated by the dedicated out-of-cycle
            // permission (Admin + HR Manager), not the generic statutory view.
            to: '/payroll/organization/settings/run-window',
            label: 'Run window',
            icon: CalendarRange,
            permission: 'payroll.run.out_of_cycle',
          },
        ],
      },
      {
        // Setup — the pay-rate/policy setup that used to sit on its own "Setup"
        // rail icon, folded into this panel as its own labelled section. Gated
        // by payroll.statutory.view, matching the old Setup rail item.
        heading: 'Setup and Configuration',
        links: [
          {
            to: '/payroll/setup/pay-schedule',
            label: 'Pay Schedule',
            icon: CalendarClock,
            permission: 'payroll.statutory.view',
          },
          {
            to: '/payroll/setup/statutory',
            label: 'Statutory Component',
            icon: Receipt,
            permission: 'payroll.statutory.view',
          },
          {
            to: '/payroll/setup/salary-components',
            label: 'Salary Component',
            icon: Coins,
            permission: 'payroll.statutory.view',
          },
          {
            to: '/payroll/setup/ot-lop-policy',
            label: 'OT & LOP Policy',
            icon: Timer,
            permission: 'payroll.statutory.view',
          },
          {
            to: '/payroll/setup/ctc-templates',
            label: 'CTC Template',
            icon: Layers,
            permission: 'payroll.statutory.view',
          },
        ],
      },
    ],
  },
  'payroll-payouts': {
    groups: [
      {
        links: [
          {
            to: '/payroll/payouts/variable-pay',
            label: 'Incentives & Bonuses',
            icon: Coins,
            permission: 'payroll.payroll_run.view',
          },
          { to: '/payroll/payouts/reimbursements', label: 'Reimbursements', icon: Receipt },
          { to: '/payroll/payouts/loans', label: 'Loans & Advances', icon: Landmark },
        ],
      },
    ],
  },
  'payroll-compliance': {
    groups: [
      // Everything that was not a tax form has left this panel:
      //   - My Payslips  → the user profile (/payroll/profile → Payslips tab),
      //     beside the rest of the employee's own data.
      //   - Past Payroll Runs → the Payroll module, which already lists every
      //     run; it is reached from that panel's "All pay periods" row.
      // What remains is one group, so it needs no heading of its own — the
      // panel header already says Tax.
      {
        links: [
          // Form 24Q is the ORG-WIDE quarterly TDS return, not self-service, so
          // it carries its own key rather than riding the rail item's
          // `tax_form.self.view` gate — an employee reaches this panel with
          // that self key alone.
          {
            to: '/payroll/tax/tds-report',
            label: 'Form 24Q (TDS)',
            icon: FileSpreadsheet,
            permission: 'payroll.tds_report.view',
          },
          { to: '/payroll/tax/form-16', label: 'Form 16', icon: FileText },
        ],
      },
    ],
  },
};

/**
 * Payroll Users counts for the sidebar sub-navigation — one call to the
 * payroll-api's `/payroll/users/status-counts` (the same endpoint the page's
 * status tabs read), so the sidebar numbers never disagree with the tabs.
 * "All" is the sum of the three statuses, matching the page's All tab.
 */
function useSidebarPayrollUsersData() {
  const [counts, setCounts] = useState({ all: 0, active: 0, deactivated: 0, verified: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiService
      .get<{ active: number; deactivated: number; invited: number; verified: number }>(
        '/payroll/users/status-counts',
      )
      .then((res) => {
        if (cancelled) return;
        setCounts({
          all: res.active + res.deactivated + res.invited,
          active: res.active,
          deactivated: res.deactivated,
          // Verified profiles — a review state that cuts across
          // active/inactive, so deliberately NOT part of the `all` sum.
          verified: res.verified ?? 0,
        });
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setIsError(true);
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { ...counts, isLoading, isError };
}

/** Payroll → Users secondary panel: All Users / Active / Deactivated /
 *  Verified with counts, deep-linking to the page's `?status=` tabs (mirrors
 *  the projects UsersPanel above). */
function PayrollUsersPanel({ onNavigate }: { onNavigate: () => void }) {
  const { all, active, deactivated, verified, isLoading, isError } = useSidebarPayrollUsersData();
  const [searchParams] = useSearchParams();
  const currentStatus = searchParams.get('status') ?? 'all';

  const rows = [
    { status: 'all', label: 'All Users', icon: UsersIcon, count: all, tone: 'gray' },
    { status: 'active', label: 'Active', icon: UserCheck, count: active, tone: 'green' },
    { status: 'deactivated', label: 'Deactivated', icon: UserX, count: deactivated, tone: 'gray' },
    { status: 'verified', label: 'Verified', icon: ShieldCheck, count: verified, tone: 'green' },
  ] as const;
  const candidates = rows.map((r) => ({
    id: `/payroll/users?status=${r.status}`,
    label: r.label,
    to: `/payroll/users?status=${r.status}`,
    active: currentStatus === r.status,
  }));

  return (
    // No "USERS" SectionLabel here — the panel chrome's own header already
    // reads "Users", so the group label was a visible duplicate.
    <div className="py-2">
      {isLoading && (
        <div className="flex justify-center py-4">
          <Loader2 className="text-muted-foreground h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
        </div>
      )}

      {!isLoading && isError && (
        <div className="px-3 py-3 text-center text-sm text-red-500">Failed to load</div>
      )}

      {!isLoading && !isError && (
        <>
          <FavouritesSection candidates={candidates} onNavigate={onNavigate} />
          <div className="mt-4 space-y-0.5 px-2 first:mt-0">
            {rows.map((r, i) => (
              <HoverNavRow
                key={r.status}
                to={candidates[i]!.to}
                onNavigate={onNavigate}
                active={candidates[i]!.active}
                favourite={{ id: candidates[i]!.id, label: r.label }}
              >
                <r.icon
                  className={cn(
                    'h-4 w-4 flex-shrink-0',
                    r.tone === 'green' ? 'text-emerald-500' : 'text-gray-400',
                  )}
                  strokeWidth={1.5}
                />
                <span className="flex-1">{r.label}</span>
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-sm font-semibold leading-none',
                    r.tone === 'green'
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-muted-foreground bg-gray-100',
                  )}
                >
                  {r.count}
                </span>
              </HoverNavRow>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ── Payroll → Pay history (the prototype's left `.hist` panel) ──
   Replaces the old "All periods + month list" Attendance panel, which died with
   the standalone Attendance module (its rail item is gone and
   /payroll/payroll/attendance now redirects — the run owns the import).

   The panel is the payroll module's primary navigation now: every pay period the
   calendar knows about, grouped Upcoming / In progress / Completed, with the
   month and its status. Clicking a period opens that run's cockpit; an Upcoming
   month is locked (it cannot be run until it arrives).

   The bucketing rules are NOT reimplemented here. They live in `@clarix/utils`
   (`payroll-period-status.ts` + `payroll-pay-history.ts`) — pure, framework-free,
   and imported by BOTH this panel and the payroll remote's Pay history page, so
   the two surfaces can never disagree about whether a month is runnable, missed
   or already paid. The shell may not import remote source (root CLAUDE.md hard
   rule 1), so a shared package is the only single-source option. */

/**
 * The run fields the sidebar rows render. It structurally satisfies the shared
 * `PayHistoryRun` contract, so `buildPayHistory` types its entries with exactly
 * this shape — the shell never needs payroll's full `PayrollRun` DTO.
 */
interface SidebarPayrollRun extends PayHistoryRun {
  id: string;
}

/**
 * One page of runs, appended as the panel is scrolled.
 *
 * The list comes back newest-first (the API sorts `pay_period_year desc,
 * pay_period_month desc`), so page 1 is the recent history and every further
 * page walks back in time. 24 ≈ two years of monthly runs, which matches the
 * timeline's own default 24-month window — an org with a normal history loads in
 * ONE request and never paginates. Kept in step with the payroll page's
 * `HISTORY_PAGE_SIZE` so the two surfaces fill in at the same rate.
 *
 * MUST NOT exceed 200: the run-list endpoint validates `page_size` with
 * `.max(200)`, and anything larger is rejected outright (422) — which surfaced as
 * the whole panel reading "Failed to load".
 */
const PAY_HISTORY_RUNS_PAGE_SIZE = 24;

/**
 * Cross-app change signals — the payroll remote fires these window CustomEvents
 * after it mutates something this panel shows: a run started
 * (pages/payroll/components/RunPayrollDialog.tsx) or an attendance period
 * opened / (un)locked (lib/attendance-periods-event.ts). The panel has its own
 * fetch in a separate React tree, so payroll's query invalidation can't reach
 * it. KEEP BOTH NAMES IN SYNC with those emitters.
 */
const ATTENDANCE_PERIODS_CHANGED_EVENT = 'payroll:attendance-periods-changed';
const PAYROLL_RUNS_CHANGED_EVENT = 'payroll:runs-changed';

/** One page of the run list, as the endpoint returns it. */
type SidebarRunsPage = { data: SidebarPayrollRun[]; pagination?: { total?: number } };

/** `GET /payroll/runs`, one page. Split out so page 1 and `loadMore` can't drift. */
function fetchSidebarRunsPage(page: number) {
  // `baseService` (not apiService): the run list carries its `pagination` block
  // OUTSIDE the `data` envelope, and the total is what tells us whether the
  // pages loaded so far are the org's full history.
  return baseService.get<SidebarRunsPage>('/payroll/runs', {
    params: { page, page_size: PAY_HISTORY_RUNS_PAGE_SIZE },
  });
}

// ── Simulations ("what-if" runs) ─────────────────────────────────────
// A SEPARATE list from the pay periods above, from a separate endpoint. The two
// are never merged: a pay period is real payroll, a simulation is a scratch pad,
// and the entire point of the feature is that the two can never be confused.

const SIMULATION_MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

interface SidebarSimulationRun {
  id: string;
  pay_period_year: number;
  pay_period_month: number;
  is_simulation: boolean;
  employee_count: number;
  total_net: number;
}

interface SidebarSimulationMonth {
  key: string;
  label: string;
  run: SidebarSimulationRun | null;
}

/**
 * Expand the saved simulation range into one entry per month, joined to its
 * simulation if one exists.
 *
 * Deliberately duplicated here rather than imported from the payroll remote: the
 * shell must never import remote source (root CLAUDE.md hard rule 1), and this is
 * plain calendar arithmetic that cannot meaningfully drift — the same reasoning
 * that already justifies the local month-label list in
 * `packages/utils/src/payroll-period-status.ts`.
 */
function buildSidebarSimulationMonths(
  from: string | null,
  to: string | null,
  runs: readonly SidebarSimulationRun[],
): SidebarSimulationMonth[] {
  const parse = (k: string | null) => {
    const m = k ? /^(\d{4})-(0[1-9]|1[0-2])$/.exec(k) : null;
    return m ? Number(m[1]) * 12 + (Number(m[2]) - 1) : null;
  };
  const startOrd = parse(from);
  const endOrd = parse(to);
  if (startOrd == null || endOrd == null || startOrd > endOrd) return [];

  const byKey = new Map<string, SidebarSimulationRun>();
  for (const r of runs) {
    if (!r.is_simulation) continue;
    byKey.set(`${r.pay_period_year}-${String(r.pay_period_month).padStart(2, '0')}`, r);
  }

  const out: SidebarSimulationMonth[] = [];
  // 120 = ten years, the same ceiling the payroll page applies, so the panel can
  // never be handed an unbounded range to render.
  for (let ord = startOrd; ord <= Math.min(endOrd, startOrd + 119); ord += 1) {
    const year = Math.floor(ord / 12);
    const month = (ord % 12) + 1;
    const key = `${year}-${String(month).padStart(2, '0')}`;
    out.push({
      key,
      label: `${SIMULATION_MONTH_LABELS[month - 1]} ${year}`,
      run: byKey.get(key) ?? null,
    });
  }
  return out;
}

/**
 * The Simulations group's data: the configured month range (from the pay
 * schedule's free-form config) joined to the simulations created so far.
 *
 * Its own fetch rather than an extension of `useSidebarPayHistory`: the two lists
 * come from different endpoints, change independently, and must never share a
 * cache — one shared loader would refetch real runs on every simulation edit and,
 * worse, put both sets of rows one bug away from being rendered in the same list.
 */
/** Stable identity, so the disabled return below doesn't allocate each render. */
const NO_SIMULATION_MONTHS: SidebarSimulationMonth[] = [];

function useSidebarSimulations(enabled: boolean) {
  const [months, setMonths] = useState<SidebarSimulationMonth[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    // No synchronous setState in the effect body — the disabled case is DERIVED
    // at the return instead (see below). Writing it into state here is what
    // `react-hooks/set-state-in-effect` flags: it schedules a second render to
    // express something that is a pure function of `enabled`.
    if (!enabled) return undefined;
    let cancelled = false;
    Promise.all([
      baseService
        .get<{ data: SidebarSimulationRun[] }>('/payroll/runs/simulations', {
          params: { page_size: 200 },
        })
        .then((r) => r.data?.data ?? [])
        .catch(() => [] as SidebarSimulationRun[]),
      apiService
        .get<{ config?: Record<string, unknown> | null }>('/payroll/organization/pay-schedule')
        .catch(() => null),
    ])
      .then(([runs, schedule]) => {
        if (cancelled) return;
        const cfg = (schedule?.config ?? null) as Record<string, unknown> | null;
        const read = (k: string) => {
          const v = cfg?.[k];
          return typeof v === 'string' ? v : null;
        };
        setMonths(
          buildSidebarSimulationMonths(read('simulation_from'), read('simulation_to'), runs),
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, reloadKey]);

  // Payroll fires this after a simulation is created or deleted — the panel lives
  // in the shell's React tree, so a query invalidation inside the remote cannot
  // reach it. KEEP THE NAME IN SYNC with `utils/constants/payroll-events.ts`.
  useEffect(() => {
    const onChanged = () => setReloadKey((k) => k + 1);
    window.addEventListener('payroll:simulations-changed', onChanged);
    return () => window.removeEventListener('payroll:simulations-changed', onChanged);
  }, []);

  // Derived, not stored: with the feature off there is nothing to load and
  // nothing to show. A refetch (reloadKey) deliberately does NOT re-raise the
  // spinner — the rows simply update in place, which is the right behaviour for a
  // background refresh after a simulation is created or deleted.
  return enabled ? { months, isLoading } : { months: NO_SIMULATION_MONTHS, isLoading: false };
}

/**
 * Runs + pay schedule for the Pay history panel — the SAME two endpoints the
 * payroll page reads (`GET /payroll/runs`, `GET /payroll/organization/pay-schedule`)
 * and the same shared rules, so once both have loaded their pages the sidebar
 * and the page render identical months. (Each paginates on its own scroll, so a
 * long history may be further along on one surface than the other — a difference
 * of how MANY months are listed, never of what a month is called.)
 *
 * Runs page in on demand (newest first), `loadMore` appending the next page when
 * the panel is scrolled to the bottom; the payroll change events reset back to
 * page 1 rather than appending.
 *
 * ── `isComplete` is the missed-month safety gate ────────────────────────────
 * `buildPayHistory` calls a past month with no run MISSED — an accusation that
 * is only sound against the org's WHOLE run history. `isComplete` is therefore
 * true ONLY once a successful response proved `loaded >= total`: it starts
 * false, is false while pages are outstanding, and stays false if a fetch fails,
 * so a month whose run merely hasn't been fetched is left off the list instead
 * of being reported as skipped.
 */
function useSidebarPayHistory() {
  const [runs, setRuns] = useState<SidebarPayrollRun[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const [schedule, setSchedule] = useState<PayHistorySchedule | null>(null);
  // Org "run past & future payroll" setting (Setup → Pay schedule → config).
  // ON = the month lock is lifted, so locked rows become runnable links.
  const [allowOutOfCycle, setAllowOutOfCycle] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [isError, setIsError] = useState(false);
  // Bumped by the payroll change events → re-runs the first-page effect.
  const [reloadKey, setReloadKey] = useState(0);

  // Refs, not state: `loadMore` reads them synchronously to merge pages, to
  // refuse a second in-flight request, and to drop the response of a page that
  // was still in flight when a refetch restarted the list (`generation`).
  const runsRef = useRef<SidebarPayrollRun[]>([]);
  const pageRef = useRef(0);
  const busyRef = useRef(false);
  const generationRef = useRef(0);

  // Page 1 — on mount and on every payroll change signal. A refetch RESETS the
  // list (it never appends), so the panel can't accumulate stale months.
  useEffect(() => {
    let cancelled = false;
    const generation = reloadKey + 1;
    generationRef.current = generation;
    busyRef.current = true;
    Promise.all([
      fetchSidebarRunsPage(1),
      // The schedule only ANCHORS the calendar (first payroll month) and fixes
      // the pay day, so a missing one must not blank the panel — it degrades to
      // null and the shared rules fall back to their defaults.
      apiService
        .get<
          PayHistorySchedule & { config?: { allow_out_of_cycle_runs?: boolean } | null }
        >('/payroll/organization/pay-schedule')
        .catch(() => null),
    ])
      .then(([runsRes, sched]) => {
        if (cancelled) return;
        const rows = runsRes.data?.data ?? [];
        const total = runsRes.data?.pagination?.total ?? rows.length;
        runsRef.current = rows;
        pageRef.current = 1;
        setRuns(rows);
        setSchedule(sched);
        setAllowOutOfCycle(Boolean(sched?.config?.allow_out_of_cycle_runs));
        // An empty page also stops paging: without that clause a stale (too
        // large) total would have the sentinel ask for the same page for ever.
        setHasMore(rows.length > 0 && rows.length < total);
        // THE gate — see the header. An org with zero runs is legitimately
        // complete (0 >= 0), so its past months DO read as missed.
        setIsComplete(rows.length >= total);
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        // `isComplete` deliberately keeps its value here: a failed reload leaves
        // the panel showing what it already had, and a first-load failure leaves
        // it false, so no month is called missed off a list we never received.
        setHasMore(false);
        setIsError(true);
        setIsLoading(false);
      })
      .finally(() => {
        if (!cancelled) busyRef.current = false;
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  // Instant refresh when payroll starts a run / changes a period. `isLoading`
  // deliberately stays false so the loaded rows survive the refetch instead of
  // flashing back to a spinner.
  useEffect(() => {
    const onChanged = () => {
      setIsError(false);
      setReloadKey((k) => k + 1);
    };
    window.addEventListener(PAYROLL_RUNS_CHANGED_EVENT, onChanged);
    window.addEventListener(ATTENDANCE_PERIODS_CHANGED_EVENT, onChanged);
    return () => {
      window.removeEventListener(PAYROLL_RUNS_CHANGED_EVENT, onChanged);
      window.removeEventListener(ATTENDANCE_PERIODS_CHANGED_EVENT, onChanged);
    };
  }, []);

  // Next page — appended when the sentinel below the list scrolls into view.
  const loadMore = useCallback(() => {
    if (busyRef.current || !hasMore) return;
    const generation = generationRef.current;
    const next = pageRef.current + 1;
    busyRef.current = true;
    setIsLoadingMore(true);
    fetchSidebarRunsPage(next)
      .then((res) => {
        // A refetch restarted the list while this page was in flight — its rows
        // belong to the previous list and must not be appended to the new one.
        if (generationRef.current !== generation) return;
        const rows = res.data?.data ?? [];
        // Dedup by id: a run started between two page loads shifts the window,
        // which would otherwise repeat a boundary row.
        const seen = new Set(runsRef.current.map((r) => r.id));
        const merged = [...runsRef.current, ...rows.filter((r) => !seen.has(r.id))];
        const total = res.data?.pagination?.total ?? merged.length;
        runsRef.current = merged;
        pageRef.current = next;
        setRuns(merged);
        setHasMore(rows.length > 0 && merged.length < total);
        setIsComplete(merged.length >= total);
      })
      .catch(() => {
        // Stop paging; the loaded rows stay usable and `isComplete` stays false,
        // so a partial list never reports an unloaded month as missed.
        if (generationRef.current !== generation) return;
        setHasMore(false);
      })
      .finally(() => {
        // The footer spinner belongs to the panel, not to this generation —
        // always clear it, or a refetch landing mid-page would leave it spinning
        // for ever. `busyRef`, by contrast, is owned by whichever fetch is
        // current: once a refetch has taken over, it clears it itself.
        setIsLoadingMore(false);
        if (generationRef.current === generation) busyRef.current = false;
      });
  }, [hasMore]);

  return {
    runs,
    isComplete,
    schedule,
    allowOutOfCycle,
    isLoading,
    isLoadingMore,
    hasMore,
    isError,
    loadMore,
  };
}

/** Section order + headings — the prototype's three `.hgrp` labels. */
const PAY_HISTORY_GROUP_ORDER: readonly PayHistoryGroup[] = [
  'upcoming',
  'in_progress',
  'completed',
] as const;

const PAY_HISTORY_GROUP_LABELS: Record<PayHistoryGroup, string> = {
  upcoming: 'Upcoming',
  in_progress: 'In progress',
  completed: 'Completed',
};

/** Sort direction per section — soonest-first for Upcoming, newest-first otherwise. */
const PAY_HISTORY_GROUP_DIR: Record<PayHistoryGroup, 1 | -1> = {
  upcoming: 1,
  in_progress: -1,
  completed: -1,
};

/**
 * Which section a Pay-history row belongs to, keyed off PAYMENT STATUS rather
 * than the calendar: a run stays "In progress" until it is fully PAID/CLOSED —
 * only then does it move to "Completed". This deliberately overrides the shared
 * `entry.group` (which is calendar/scope-based, so a not-yet-paid run sitting
 * before the pay-schedule anchor would fall into Completed). Run-less months
 * fall back to the calendar: a future month is Upcoming, the current month is In
 * progress (its "not started" card), and a past month with no run is Completed
 * history. The Pay-history PAGE uses the SAME rule, so the two never disagree.
 */
function payHistoryStatusGroup(entry: PayHistoryEntry<SidebarPayrollRun>): PayHistoryGroup {
  if (entry.run) {
    return entry.run.status === 'PAID' || entry.run.status === 'CLOSED'
      ? 'completed'
      : 'in_progress';
  }
  if (entry.status.bucket === 'upcoming') return 'upcoming';
  if (entry.status.bucket === 'current') return 'in_progress';
  return 'completed';
}

/**
 * Status dot tint for a row — the sidebar's equivalent of the page's status
 * chip (a 224px panel has no room for a pill, and the words are on line 2).
 * Hues mirror the page's `STATUS_TONE` map.
 */
function payHistoryDotClass(entry: PayHistoryEntry<SidebarPayrollRun>): string {
  if (entry.run) {
    switch (entry.run.status) {
      case 'PAID':
        return 'bg-emerald-500';
      case 'APPROVED':
        return 'bg-violet-500';
      case 'PROCESSED':
        return 'bg-blue-500';
      default:
        // DRAFT / CLOSED — nothing to signal.
        return 'bg-gray-400';
    }
  }
  if (entry.status.warning === 'missed') return 'bg-amber-500';
  if (entry.status.bucket === 'upcoming') return 'bg-gray-300';
  // The current month, not started yet — the one row that wants attention.
  return 'bg-indigo-400';
}

/** The words on a row's second line: the run's state, or the month's. */
function payHistoryStatusText(entry: PayHistoryEntry<SidebarPayrollRun>): string {
  return entry.run
    ? PAYROLL_RUN_STATUS_LABEL[entry.run.status]
    : payHistoryPeriodLabel(entry.status);
}

/**
 * Status ICON for a row that has a run — replaces the colour dot with a shape so
 * the state reads without relying on colour alone. Only DRAFT is editable, so it
 * gets a pencil; every other lifecycle state has its inputs locked, so it gets a
 * padlock (a paid/closed run is "done" → lock, exactly as asked). The dot's hue
 * is kept as the icon tint, so the finer sub-state (blue = inputs locked, violet
 * = approved, emerald = paid) is still distinguishable at a glance. Months with
 * NO run (upcoming / current / missed) keep the plain dot — they aren't payroll
 * states, and upcoming already carries its own lock affordance on the right.
 */
function payHistoryStatusIcon(
  entry: PayHistoryEntry<SidebarPayrollRun>,
): { Icon: LucideIcon; className: string } | null {
  if (!entry.run) return null;
  switch (entry.run.status) {
    case 'DRAFT':
      return { Icon: PencilLine, className: 'text-gray-400' };
    case 'PROCESSED':
      return { Icon: Lock, className: 'text-blue-500' };
    case 'APPROVED':
      return { Icon: Lock, className: 'text-violet-500' };
    case 'PAID':
      return { Icon: Lock, className: 'text-emerald-500' };
    case 'CLOSED':
      return { Icon: Lock, className: 'text-gray-400' };
    default:
      return { Icon: PencilLine, className: 'text-gray-400' };
  }
}

/**
 * One month in the Pay history list.
 *
 * A month with a run navigates to its cockpit (read-only once paid). The current
 * month with nothing started navigates to the Pay history page, where the
 * "Run payroll" confirm dialog lives — a run is a financial document, so it is
 * never created from a sidebar click. Upcoming and missed months are LOCKED and
 * render as plain, non-interactive rows.
 */
function PayHistoryRow({
  entry,
  query,
  active,
  allowOutOfCycle,
  onNavigate,
}: {
  entry: PayHistoryEntry<SidebarPayrollRun>;
  query: string;
  active: boolean;
  /** Org "run past & future payroll" is on — locked months become runnable. */
  allowOutOfCycle: boolean;
  onNavigate: () => void;
}) {
  // A month with a run shows a status ICON (pencil = draft/editable, padlock =
  // inputs locked / approved / paid / closed); a month with no run keeps the
  // plain colour dot.
  const statusIcon = payHistoryStatusIcon(entry);
  let lead;
  if (statusIcon) {
    const Icon = statusIcon.Icon;
    lead = (
      <Icon
        className={cn('mt-0.5 h-3.5 w-3.5 flex-shrink-0 self-start', statusIcon.className)}
        strokeWidth={1.75}
        aria-hidden="true"
      />
    );
  } else {
    lead = (
      <span
        className={cn(
          'mt-[5px] h-1.5 w-1.5 flex-shrink-0 self-start rounded-full',
          payHistoryDotClass(entry),
        )}
        aria-hidden="true"
      />
    );
  }

  const body = (
    <span className="flex min-w-0 flex-1 flex-col leading-tight">
      {/* A month holds two runs — its regular payroll and its Full & Final —
          and both are rows here. Without the chip the pair reads as one month
          listed twice, with no way to tell which is which.
          The month name keeps the truncation; the chip is `shrink-0` so it
          survives a long label instead of being the first thing clipped. */}
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="truncate text-lg">
          <HighlightText text={entry.label} query={query} />
        </span>
        {entry.isFnf ? (
          <Badge tone="violet" size="xs" className="shrink-0">
            F&amp;F
          </Badge>
        ) : null}
      </span>
      <span className="text-muted-foreground truncate text-xs">
        {payHistoryStatusText(entry)} · Pay date {formatPayDate(entry.status.payDate)}
      </span>
    </span>
  );

  // A locked month with no run is non-interactive — UNLESS the org has turned on
  // "run past & future payroll", which unlocks it (it becomes a runnable link
  // below, carrying its year/month to the payroll landing).
  if (entry.status.locked && !entry.run && !allowOutOfCycle) {
    // Locked months explain themselves on hover: an upcoming month says when it
    // unlocks, a missed one says what happened (the shared rules own the copy).
    const reason =
      periodWarningMessage(entry.status, entry.label) ??
      `${entry.label} unlocks on ${entry.status.unlocksOn ? formatPayDate(entry.status.unlocksOn) : entry.label} — payroll runs the current month only.`;
    return (
      <div className={cn(PANEL_ROW_CLASS, 'text-gray-400')} aria-disabled="true" title={reason}>
        {lead}
        {body}
        <Lock
          className="h-3 w-3 flex-shrink-0 text-gray-300"
          strokeWidth={1.5}
          aria-hidden="true"
        />
      </div>
    );
  }

  // A month with a run opens it; a no-run month goes to the landing. An unlocked
  // out-of-cycle month carries its year/month so the landing starts THAT month.
  const to = entry.run
    ? `/payroll/payroll/runs/${entry.run.id}`
    : entry.status.locked
      ? `/payroll/payroll/runs?year=${entry.year}&month=${entry.month}`
      : '/payroll/payroll/runs';

  return (
    <HoverNavRow to={to} onNavigate={onNavigate} active={active}>
      {lead}
      {body}
      {/* Hover affordance — the row navigates (same as the meetings rows). */}
      <ChevronRight
        className="h-3.5 w-3.5 flex-shrink-0 text-gray-300 opacity-0 transition-opacity group-hover:opacity-100"
        strokeWidth={1.5}
        aria-hidden="true"
      />
    </HoverNavRow>
  );
}

type RunStatus = keyof typeof PAYROLL_RUN_STATUS_LABEL;

/** Status + year filter for the pay-history list — mirrors the CalendarPanel
 *  filter popover. Multi-selects each; the active state shows a dot on the
 *  funnel. Sits next to the "Pay periods" heading (i.e. above "Upcoming"). */
function PayHistoryFilterPopover({
  years,
  selectedStatuses,
  selectedYears,
  onToggleStatus,
  onToggleYear,
  onClear,
}: {
  years: number[];
  selectedStatuses: RunStatus[];
  selectedYears: number[];
  onToggleStatus: (s: RunStatus) => void;
  onToggleYear: (y: number) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const active = selectedStatuses.length > 0 || selectedYears.length > 0;
  const statuses = Object.keys(PAYROLL_RUN_STATUS_LABEL) as RunStatus[];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Filter pay periods"
          className={cn(
            'relative flex h-5 w-5 flex-shrink-0 items-center justify-center rounded transition-colors',
            active
              ? 'text-indigo-600 hover:bg-indigo-50'
              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600',
          )}
        >
          <SlidersHorizontal className="h-3 w-3" strokeWidth={1.5} />
          {active && (
            <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-indigo-500" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent side="right" align="start" sideOffset={8} className="w-48 p-0">
        <div className="py-1.5">
          <div className="text-muted-foreground px-3 pb-1.5 pt-1 text-xs font-semibold uppercase tracking-wider">
            Status
          </div>
          {statuses.map((s) => {
            const sel = selectedStatuses.includes(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => onToggleStatus(s)}
                className={cn(
                  'flex w-full items-center px-3 py-1.5 text-lg transition-colors',
                  sel ? 'bg-indigo-50/70 text-indigo-700' : 'text-foreground hover:bg-gray-50',
                )}
              >
                <span
                  className={cn(
                    'mr-2.5 flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded border transition-colors',
                    sel ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300 bg-white',
                  )}
                >
                  {sel && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
                </span>
                <span className="flex-1 text-left">{PAYROLL_RUN_STATUS_LABEL[s]}</span>
              </button>
            );
          })}
          {years.length > 0 && <div className="mx-3 my-1.5 border-t border-gray-100" />}
          {years.length > 0 && (
            <div className="text-muted-foreground px-3 pb-1.5 pt-1 text-xs font-semibold uppercase tracking-wider">
              Year
            </div>
          )}
          {years.map((y) => {
            const sel = selectedYears.includes(y);
            return (
              <button
                key={y}
                type="button"
                onClick={() => onToggleYear(y)}
                className={cn(
                  'flex w-full items-center px-3 py-1.5 text-lg transition-colors',
                  sel ? 'bg-indigo-50/70 text-indigo-700' : 'text-foreground hover:bg-gray-50',
                )}
              >
                <span
                  className={cn(
                    'mr-2.5 flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded border transition-colors',
                    sel ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300 bg-white',
                  )}
                >
                  {sel && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
                </span>
                <span className="flex-1 text-left">{y}</span>
              </button>
            );
          })}
          {active && (
            <>
              <div className="mx-3 my-1.5 border-t border-gray-100" />
              <button
                type="button"
                onClick={onClear}
                className="w-full px-3 py-1.5 text-left text-lg text-indigo-600 transition-colors hover:bg-indigo-50"
              >
                Clear all filters
              </button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Payroll → Payroll secondary panel: the "Pay history" list (Upcoming /
 *  In progress / Completed), each month deep-linking to its run cockpit. */
function PayrollPayHistoryPanel({ onNavigate }: { onNavigate: () => void }) {
  const {
    runs,
    isComplete,
    schedule,
    allowOutOfCycle: scheduleAllowsOutOfCycle,
    isLoading,
    isLoadingMore,
    hasMore,
    isError,
    loadMore,
  } = useSidebarPayHistory();
  const { pathname } = useLocation();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<RunStatus[]>([]);
  const [yearFilter, setYearFilter] = useState<number[]>([]);

  // Out-of-cycle month links unlock only when BOTH the org opted in (schedule
  // config) AND this user holds the out-of-cycle permission (Admin / HR
  // Manager). Same gate as the settings page and the re-run button, so the
  // whole capability is permission-scoped, not just org-scoped.
  const { permissions, loadedApps } = usePermissionsSlice();
  const canRunOutOfCycle =
    loadedApps.payroll === true && permissions['payroll.run.out_of_cycle'] === true;
  // The Simulations group is visible ONLY to holders of the simulate permission —
  // it is a separate capability from running real payroll, and someone without it
  // should not even learn the feature exists.
  const canSimulate = loadedApps.payroll === true && permissions['payroll.run.simulate'] === true;
  const { months: simulationMonths, isLoading: simulationsLoading } =
    useSidebarSimulations(canSimulate);
  const navigate = useNavigate();
  const allowOutOfCycle = scheduleAllowsOutOfCycle && canRunOutOfCycle;

  // "Today" is resolved once per mount and threaded through the pure helpers, so
  // every row on screen is judged against the same date.
  const today = useMemo(() => new Date(), []);

  const entries = useMemo(
    () =>
      buildPayHistory<SidebarPayrollRun>({
        runs,
        today,
        schedule,
        // THE missed-month guard: only claim a month was skipped once every page
        // of runs is loaded. While pages are outstanding (or a fetch failed) a
        // month with no run yet FETCHED is simply not listed, never accused.
        hasFullHistory: isComplete,
      }),
    [runs, isComplete, today, schedule],
  );

  // Filtered IN MEMORY, unlike the old attendance panel's server-side search:
  // `GET /payroll/runs` takes no search term, so there is nothing to delegate.
  // It only ever sees the months LOADED so far — which is why the sentinel below
  // stays mounted even when the filtered list is short or empty: a search that
  // matches nothing yet keeps the sentinel in view, so the remaining pages pull
  // themselves in and the term ends up searching the whole history.
  const query = search.trim();
  const yearOptions = useMemo(
    () => Array.from(new Set(entries.map((e) => e.year))).sort((a, b) => b - a),
    [entries],
  );
  const visible = useMemo(() => {
    const needle = query.toLowerCase();
    return entries.filter((e) => {
      if (needle && !e.label.toLowerCase().includes(needle)) return false;
      if (yearFilter.length > 0 && !yearFilter.includes(e.year)) return false;
      // Status only matches run-bearing months; picking a status hides
      // upcoming/current/missed months (they have no run status).
      if (statusFilter.length > 0 && !(e.run && statusFilter.includes(e.run.status as RunStatus)))
        return false;
      return true;
    });
  }, [entries, query, statusFilter, yearFilter]);

  const groups = useMemo(
    () =>
      PAY_HISTORY_GROUP_ORDER.map((group) => ({
        group,
        // Group by PAYMENT STATUS (paid ⇒ Completed, otherwise In progress), not
        // the shared calendar bucket — see `payHistoryStatusGroup`. Re-sorted per
        // section since a row can move between buckets vs the shared ordering.
        items: visible
          .filter((e) => payHistoryStatusGroup(e) === group)
          .sort(
            (a, b) =>
              PAY_HISTORY_GROUP_DIR[group] * (a.year * 12 + a.month - (b.year * 12 + b.month)),
          ),
      })).filter((g) => g.items.length > 0),
    [visible],
  );

  return (
    <div className="py-2">
      {/* Search leads the panel, as in the engine-driven panels; the page link sits under it. */}
      <SidebarSearchInput value={search} onChange={setSearch} placeholder="Search period..." />

      <div className="mt-2 space-y-0.5 px-2">
        <HoverNavRow
          to="/payroll/payroll/payroll-history"
          onNavigate={onNavigate}
          active={pathname === '/payroll/payroll/payroll-history'}
        >
          <PanelRowIcon icon={LayoutList} />
          <span className="flex-1">All pay periods</span>
        </HoverNavRow>
      </div>

      {!isLoading && !isError && (
        <PanelSectionHeader
          className="mt-4"
          label="Pay periods"
          control={
            <PayHistoryFilterPopover
              years={yearOptions}
              selectedStatuses={statusFilter}
              selectedYears={yearFilter}
              onToggleStatus={(s) =>
                setStatusFilter((cur) =>
                  cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s],
                )
              }
              onToggleYear={(y) =>
                setYearFilter((cur) => (cur.includes(y) ? cur.filter((x) => x !== y) : [...cur, y]))
              }
              onClear={() => {
                setStatusFilter([]);
                setYearFilter([]);
              }}
            />
          }
        />
      )}

      {isLoading && (
        <div className="flex justify-center py-4">
          <Loader2 className="text-muted-foreground h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
        </div>
      )}

      {!isLoading && isError && (
        <div className="px-3 py-3 text-center text-sm text-red-500">Failed to load</div>
      )}

      {!isLoading && !isError && groups.length === 0 && (
        <div className="text-muted-foreground px-3 py-3 text-center text-sm">
          {query || statusFilter.length > 0 || yearFilter.length > 0
            ? 'No periods match your filters'
            : 'No pay periods yet — set the first payroll month in Setup → Pay Schedule.'}
        </div>
      )}

      {!isLoading && !isError && (
        <>
          {groups.map(({ group, items }) => (
            <div key={group} className="mb-1 px-2">
              <div className="mb-1 px-2 pt-3">
                <SectionLabel>{PAY_HISTORY_GROUP_LABELS[group]}</SectionLabel>
              </div>
              <div className="space-y-0.5">
                {items.map((entry) => (
                  <PayHistoryRow
                    key={entry.key}
                    entry={entry}
                    query={query}
                    allowOutOfCycle={allowOutOfCycle}
                    // Only a row that OWNS a run can be the active route — the
                    // "not started" row links to the list page, which is already
                    // highlighted by "All pay periods" above.
                    active={!!entry.run && pathname === `/payroll/payroll/runs/${entry.run.id}`}
                    onNavigate={onNavigate}
                  />
                ))}
              </div>
            </div>
          ))}

          {/* ── Simulations ─────────────────────────────────────────────
              A group of its OWN, below the pay periods, never merged into them.
              Every row carries the flask glyph and amber accent so a what-if month
              can never be mistaken for the real month of the same name sitting a
              few rows above it — the two lists genuinely do show "March 2027"
              twice, and only the treatment tells them apart. */}
          {canSimulate && (
            <div className="mb-1 px-2">
              <div className="mb-1 flex items-center justify-between gap-2 px-2 pt-3">
                <SectionLabel>Simulations</SectionLabel>
                {/* A plain button, not a HoverNavRow: that component is a
                    full-width row and this has to sit inline beside the heading. */}
                <button
                  type="button"
                  className="flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                  onClick={() => {
                    navigate('/payroll/payroll/simulations');
                    onNavigate();
                  }}
                >
                  <Plus className="h-3 w-3" strokeWidth={2} aria-hidden />
                  New
                </button>
              </div>

              {simulationsLoading ? (
                <div className="flex justify-center py-2">
                  <Loader2
                    className="text-muted-foreground h-3.5 w-3.5 animate-spin"
                    strokeWidth={1.5}
                  />
                </div>
              ) : simulationMonths.length === 0 ? (
                <div className="text-muted-foreground px-2.5 py-2 text-sm">
                  No simulation months yet — pick a month range on the Simulations page.
                </div>
              ) : (
                <div className="space-y-0.5">
                  {simulationMonths.map((entry) => (
                    <HoverNavRow
                      key={entry.key}
                      to={
                        entry.run
                          ? `/payroll/payroll/simulations/${entry.run.id}`
                          : '/payroll/payroll/simulations'
                      }
                      onNavigate={onNavigate}
                      active={
                        !!entry.run && pathname === `/payroll/payroll/simulations/${entry.run.id}`
                      }
                    >
                      <FlaskConical
                        className="h-3.5 w-3.5 flex-shrink-0 text-amber-500"
                        strokeWidth={1.5}
                        aria-hidden="true"
                      />
                      <span className="flex min-w-0 flex-1 flex-col leading-tight">
                        <span className="truncate text-lg">
                          <HighlightText text={entry.label} query={query} />
                        </span>
                        <span className="text-muted-foreground truncate text-xs">
                          {entry.run ? 'Simulation · not real payroll' : 'Not simulated yet'}
                        </span>
                      </span>
                    </HoverNavRow>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Next-page trigger — OUTSIDE the groups, after the last one, so it is
              the final thing in the panel's scroll container. It is rendered even
              when the list is empty (a filtered-out or still-unloaded bucket):
              with nothing to scroll it simply sits in view and keeps pulling
              pages until the history is complete. Swapped for a spinner while a
              page is in flight, so it can't fire twice for the same page. */}
          {isLoadingMore ? (
            <div className="flex justify-center py-2">
              <Loader2
                className="text-muted-foreground h-3.5 w-3.5 animate-spin"
                strokeWidth={1.5}
              />
            </div>
          ) : (
            <InfiniteScrollSentinel enabled={hasMore} onLoadMore={loadMore} />
          )}
        </>
      )}
    </div>
  );
}

function PayrollPanel({ panelId, onNavigate }: { panelId: string; onNavigate: () => void }) {
  const { pathname } = useLocation();
  const { permissions, loadedApps } = usePermissionsSlice();
  const config = PAYROLL_PANELS[panelId];
  if (!config) return null;

  // Filter permission-tagged links only once the payroll remote has published
  // its matrix; groups whose links all filter out drop entirely (no orphan headings).
  const payrollLoaded = loadedApps.payroll === true;
  const visibleGroups = config.groups
    .map((group) => ({
      ...group,
      links: group.links.filter(
        (link) => !link.permission || !payrollLoaded || permissions[link.permission] === true,
      ),
    }))
    .filter((group) => group.links.length > 0);

  const isActive = (link: PayrollNavLink) =>
    link.exact ? pathname === link.to : pathname === link.to || pathname.startsWith(`${link.to}/`);
  const candidates = visibleGroups.flatMap((group) =>
    group.links.map((link) => ({
      id: link.to,
      label: link.label,
      to: link.to,
      active: isActive(link),
    })),
  );

  return (
    <div className="py-2">
      <FavouritesSection candidates={candidates} onNavigate={onNavigate} />
      {visibleGroups.map((group, gi) => (
        <div
          key={group.heading ?? `group-${gi}`}
          className={cn((gi > 0 || group.heading) && 'mt-4 first:mt-0')}
        >
          <PanelSectionHeader label={group.heading} />
          <div className="space-y-0.5 px-2">
            {group.links.map((link) => (
              <HoverNavRow
                key={link.to}
                to={link.to}
                onNavigate={onNavigate}
                active={isActive(link)}
                favourite={{ id: link.to, label: link.label }}
              >
                <PanelRowIcon icon={link.icon} />
                <span className="flex-1 truncate">{link.label}</span>
              </HoverNavRow>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── PMS Settings ──
   Mirrors the prototype exactly (prototypes/dumbledore/index.html): ONE group
   labelled "Master" holding Categories, Competencies and Performance bands,
   each with a live count — the prototype's SCREEN_NAV supplies an icon and a
   count() per row, the same treatment the Workspace app gives its masters.

   Deliberately NOT here: the designation matrix (it is step 1 of the Forms
   wizard, FORM_STEPS[0]) and bulk import (a dialog opened from each master's
   own action bar). Both were placed under Settings from the PRD's module
   structure and that was wrong — for placement the prototype is the authority. */

const PMS_MASTER_SECTIONS = [
  { to: '/pms/settings/categories', label: 'Categories', icon: Grip },
  { to: '/pms/settings/competencies', label: 'Competencies', icon: Layers },
  { to: '/pms/settings/bands', label: 'Performance bands', icon: BarChart2 },
] as const;

/**
 * Access sits in its own group, not under Master: it configures WHO may use the
 * app rather than what the app measures (M8, PRD §13).
 */
const PMS_ACCESS_SECTIONS = [
  { to: '/pms/settings/roles', label: 'Roles & permissions', icon: ShieldCheck },
  /**
   * Read-only LLM usage + cost ledger (contract §15). Filed under Access rather
   * than Masters: it is an admin oversight surface, not something the form builder
   * configures.
   *
   * `permission` is per-entry because this row does NOT share the group's gate.
   * The group is shown on `pms.rbac.view`; this page needs `pms.ai.audit.view`.
   * Today only Admin holds both, so they agree — but that is coincidence, and a
   * future role with rbac.view alone would see a link that answers 403, which is
   * exactly what the group gate exists to prevent.
   */
  {
    // The GENERAL audit trail. It was the AI log until PMS started recording
    // anything else; AI generations are now one event type within it.
    to: '/pms/settings/audit',
    label: 'Audit log',
    icon: ScrollText,
    permission: 'pms.ai.audit.view',
  },
  {
    /**
     * The notification master (D-25) — per-type in-app and email toggles.
     *
     * Gated on `pms.masters.view` for the same reason the row above carries its
     * own key: this is workspace-wide CONFIGURATION, like bands and the matrix,
     * and it does not share the group's `pms.rbac.view` gate. Someone who
     * administers roles is not automatically someone who decides what the
     * product mails people.
     */
    to: '/pms/settings/notifications',
    label: 'Notifications',
    icon: BellRing,
    permission: 'pms.masters.view',
  },
] as const;

/* ── PMS Forms panel ──
   The Forms rail icon fronts a hover panel that lists every form — the same
   treatment the Settings panel gives its master sections (SectionLabel +
   HoverNavRow rows, a `SidebarSearchInput` once the list is long enough to
   warrant it). The lead row returns to the register (`/pms/forms`); each form
   row opens its wizard (`/pms/forms/:id`). */

function PmsFormsPanel({ onNavigate }: { onNavigate: () => void }) {
  const { pathname } = useLocation();
  const { data: forms = [], isLoading } = usePmsForms();
  const [search, setSearch] = useState('');

  /**
   * Designations come from WORKSPACE, not PMS.
   *
   * `designation_name` on a form is optional and the API never fills it — the
   * serializer lists it as unsatisfied, because it needs a cross-schema lookup
   * PMS may not make. The register resolves the id through the workspace masters,
   * so this must too: without it every generated name falls past its designation
   * to "Untitled form" and the whole panel reads identically.
   */
  const slug = useAuthStore((st) => st.workspaceSlug);
  const masters = useWorkspaceMasters(slug ?? null);
  const titleOf = useMemo(() => {
    const byId = new Map<string, string>();
    for (const r of masters.data?.rows ?? []) {
      if (r.category === 'designation') byId.set(r.id, r.name);
    }
    return (f: FormTemplate) =>
      formTitle(
        f.name,
        f.designation_name ?? (f.designation_id ? byId.get(f.designation_id) : null),
      );
  }, [masters.data]);

  const q = search.trim().toLowerCase();
  const visible = useMemo(
    () => (q ? forms.filter((f) => titleOf(f).toLowerCase().includes(q)) : forms),
    [forms, q, titleOf],
  );

  // `/pms/forms/:id` is a specific form open in the wizard.
  const isFormActive = (id: string) => pathname.startsWith(`/pms/forms/${id}`);

  const { data: runs = [] } = usePmsRuns();
  const runById = useMemo(() => new Map(runs.map((r) => [r.run_id, r])), [runs]);

  /** Green while a cycle is running, grey while the form is a draft; nothing once it is done. */
  const cycleDot = (f: FormTemplate) => {
    const run = f.latest_run_id ? runById.get(f.latest_run_id) : undefined;
    const state = formCycleState(f, run);
    const tone =
      state === 'live' || state === 'scheduled'
        ? 'bg-success'
        : state === 'draft'
          ? 'bg-muted-foreground/40'
          : null;
    if (!tone) return null;
    const label = FORM_CYCLE_LABEL[state];
    return (
      <span
        role="img"
        aria-label={label}
        title={label}
        className={cn('h-1.5 w-1.5 flex-shrink-0 rounded-full', tone)}
      />
    );
  };

  return (
    <div className="py-2">
      {/* No "FORMS" SectionLabel: the panel chrome's own header already reads
          "Forms", so a group label would be a visible duplicate (the same
          reason the Users panel drops its label). */}

      {/* "All forms" clears the selection and lands on the register — the same
          escape hatch the My Team panel opens with. It stays ABOVE the search
          so "show everything" is always the first row in the panel, whatever
          the field below it is filtering. */}
      <div className="space-y-0.5 px-2">
        <HoverNavRow to="/pms/forms" onNavigate={onNavigate} active={pathname === '/pms/forms'}>
          <PanelRowIcon icon={LayoutList} />
          <span className="flex-1">All forms</span>
          <span className="text-muted-foreground rounded-full bg-gray-100 px-2 py-0.5 text-sm font-semibold leading-none">
            {forms.length}
          </span>
        </HoverNavRow>
      </div>

      {/* Search is ALWAYS offered, not only past a length threshold.
          It used to appear only above six forms, so on a workspace with five
          the field was simply absent — and a control that comes and goes with
          the data reads as missing rather than as unnecessary. It costs one row
          and it is where anyone looks first.

          It sits BETWEEN the All row and the list: it scopes the list below it,
          not the escape hatch above it. */}
      <div className="mt-1.5">
        <SidebarSearchInput value={search} onChange={setSearch} placeholder="Search forms..." />
      </div>

      <div className="space-y-0.5 px-2">
        {isLoading ? (
          <div className="flex justify-center py-6 text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
          </div>
        ) : visible.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-gray-500">
            {forms.length ? 'No form matches your search.' : 'No forms yet.'}
          </p>
        ) : (
          visible.map((f) => (
            <HoverNavRow
              key={f.template_id}
              to={`/pms/forms/${f.template_id}`}
              onNavigate={onNavigate}
              active={isFormActive(f.template_id)}
            >
              <PanelRowIcon icon={FileEdit} />
              <HighlightText text={titleOf(f)} query={search} className="flex-1 truncate" />
              {cycleDot(f)}
              <span className="text-muted-foreground ml-auto flex-shrink-0 text-xs tabular-nums">
                v{f.version}
              </span>
            </HoverNavRow>
          ))
        )}
      </div>
    </div>
  );
}

/* ── PMS Users panel ──
   The Users rail icon fronts a hover panel that groups the directory by PMS role
   with live counts — the payroll Users panel's shape (an "All" row then a row per
   bucket, each with its count pill), here keyed on role rather than status. Each
   row deep-links the Users page's role filter via `?role=`; the page reads that
   param as its active tab. A trailing "Needs evaluator access" row appears only
   when there is something to fix. */
function PmsUsersPanel({ onNavigate }: { onNavigate: () => void }) {
  const [searchParams] = useSearchParams();
  const activeRole = searchParams.get('role') ?? 'all';
  const { data: users = [], isLoading, isError } = usePmsUserRoles();
  const { data: templates = [] } = usePmsRoleTemplates();

  const countFor = (roleKey: string) => users.filter((u) => (u.role_key ?? '—') === roleKey).length;
  const needsEvaluator = users.filter(
    (u) => u.reportee_count > 0 && !u.can_evaluate && !u.has_full_access,
  ).length;

  const roleHref = (roleKey: string) => `/pms/users?role=${encodeURIComponent(roleKey)}`;
  const candidates = [
    { id: '/pms/users', label: 'All users', to: '/pms/users', active: activeRole === 'all' },
    ...templates.map((t) => ({
      id: roleHref(t.role_key),
      label: t.label,
      to: roleHref(t.role_key),
      active: activeRole === t.role_key,
    })),
  ];

  return (
    // No "USERS" SectionLabel — the panel chrome's own header already reads
    // "Users", so a group label would be a visible duplicate (payroll's rule).
    <div className="py-2">
      {isLoading && (
        <div className="flex justify-center py-4">
          <Loader2 className="text-muted-foreground h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
        </div>
      )}

      {!isLoading && isError && (
        <div className="px-3 py-3 text-center text-sm text-red-500">Failed to load</div>
      )}

      {!isLoading && !isError && (
        <FavouritesSection candidates={candidates} onNavigate={onNavigate} />
      )}

      {!isLoading && !isError && (
        <div className="mt-4 space-y-0.5 px-2 first:mt-0">
          <HoverNavRow
            to="/pms/users"
            onNavigate={onNavigate}
            active={activeRole === 'all'}
            favourite={{ id: '/pms/users', label: 'All users' }}
          >
            <PanelRowIcon icon={UsersIcon} />
            <span className="flex-1">All users</span>
            <span className="text-muted-foreground rounded-full bg-gray-100 px-2 py-0.5 text-sm font-semibold leading-none">
              {users.length}
            </span>
          </HoverNavRow>

          {templates.map((t) => (
            <HoverNavRow
              key={t.role_key}
              to={roleHref(t.role_key)}
              onNavigate={onNavigate}
              active={activeRole === t.role_key}
              favourite={{ id: roleHref(t.role_key), label: t.label }}
            >
              <PanelRowIcon icon={ShieldCheck} />
              <span className="flex-1 truncate">{t.label}</span>
              <span className="text-muted-foreground rounded-full bg-gray-100 px-2 py-0.5 text-sm font-semibold leading-none">
                {countFor(t.role_key)}
              </span>
            </HoverNavRow>
          ))}

          {needsEvaluator > 0 && (
            <HoverNavRow
              to="/pms/users?role=__needs_evaluator__"
              onNavigate={onNavigate}
              active={activeRole === '__needs_evaluator__'}
            >
              <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-500" strokeWidth={1.5} />
              <span className="flex-1 truncate">Needs evaluator access</span>
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-sm font-semibold leading-none text-amber-700">
                {needsEvaluator}
              </span>
            </HoverNavRow>
          )}
        </div>
      )}
    </div>
  );
}

/* ── PMS My Team panel ──
   The My Team rail icon fronts a hover panel listing the evaluator's team — one
   row per reportee, by name. Clicking a teammate does NOT open their form; it
   FILTERS the My Team table to that person via `?member=<employee_id>` (an "All"
   row at the top clears it), the same way the Users panel filters by role. The
   team feed carries one row PER participant (per run), so it is de-duped to
   unique people here. */
function PmsMyTeamPanel({ onNavigate }: { onNavigate: () => void }) {
  const [searchParams] = useSearchParams();
  const activeMember = searchParams.get('member') ?? 'all';
  const { data: rows = [], isLoading } = usePmsTeamEvaluations();
  const [search, setSearch] = useState('');

  const memberName = (r: TeamEvaluationRow) => r.employee_name ?? r.employee_id;

  // One entry per person — the feed repeats a member across runs, and a panel
  // that listed the same name three times would read as a bug.
  const members = useMemo(() => {
    const seen = new Set<string>();
    const out: TeamEvaluationRow[] = [];
    for (const r of rows) {
      if (seen.has(r.employee_id)) continue;
      seen.add(r.employee_id);
      out.push(r);
    }
    return out;
  }, [rows]);

  const q = search.trim().toLowerCase();
  const visible = useMemo(
    () => (q ? members.filter((r) => memberName(r).toLowerCase().includes(q)) : members),
    [members, q],
  );

  return (
    // No "MY TEAM" SectionLabel — the panel chrome's own header already reads
    // "My Team", so a group label would be a visible duplicate (the Users rule).
    <div className="py-2">
      {isLoading ? (
        <div className="flex justify-center py-6 text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
        </div>
      ) : (
        <>
          {/* "All" clears the member filter — the whole team queue. It stays
              ABOVE the search so the "show everyone" escape hatch is always the
              first thing in the panel, whatever the search box is filtering. */}
          <div className="space-y-0.5 px-2">
            <HoverNavRow to="/pms/my-team" onNavigate={onNavigate} active={activeMember === 'all'}>
              <PanelRowIcon icon={UsersIcon} />
              <span className="flex-1">All members</span>
              <span className="text-muted-foreground rounded-full bg-gray-100 px-2 py-0.5 text-sm font-semibold leading-none">
                {members.length}
              </span>
            </HoverNavRow>
          </div>

          {/* Search sits BETWEEN the All row and the member list — it scopes the
              list below it, not the "All" escape hatch above it. */}
          <div className="mt-1.5">
            <SidebarSearchInput value={search} onChange={setSearch} placeholder="Search team…" />
          </div>

          <div className="space-y-0.5 px-2">
            {visible.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-gray-500">
                {members.length
                  ? 'No teammate matches your search.'
                  : 'No one is assigned to you yet.'}
              </p>
            ) : (
              visible.map((r) => (
                <HoverNavRow
                  key={r.employee_id}
                  to={`/pms/my-team?member=${encodeURIComponent(r.employee_id)}`}
                  onNavigate={onNavigate}
                  active={activeMember === r.employee_id}
                >
                  <PanelRowIcon icon={UserCheck} />
                  <span className="flex-1 truncate">{memberName(r)}</span>
                </HoverNavRow>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

function PmsSettingsPanel({ onNavigate }: { onNavigate: () => void }) {
  const { pathname } = useLocation();
  // `loaded` false → show the Access group. PMS publishes its matrix on mount, so
  // this is a brief window, and flashing a link is better than hiding the only way
  // to fix a permission problem.
  const { permissions, loaded } = usePermissionsSlice();
  const categories = usePmsCategories();
  const competencies = usePmsCompetencies();
  const bands = usePmsBands();

  // Keyed by route so a row and its count cannot drift apart.
  const counts: Record<string, number | undefined> = {
    '/pms/settings/categories': categories.data?.length,
    '/pms/settings/competencies': competencies.data?.length,
    '/pms/settings/bands': bands.data?.length,
  };

  // `/pms/settings` alone redirects to Roles & permissions (the sidebar's first
  // group), so treat the bare path as Roles active rather than leaving every row
  // unlit for the redirect's brief moment.
  const settingsRoot = pathname.replace(/\/+$/, '') === '/pms/settings';
  const isActive = (to: string) =>
    pathname.startsWith(to) || (settingsRoot && to.endsWith('/roles'));

  // Access leads the panel (it governs WHO may use the app), then Master. Access
  // is gated on rbac.view, so a non-manager does not see a link that answers 403 —
  // when it is hidden, Master becomes the first group and drops its top gap.
  const showAccess = !loaded || permissions['pms.rbac.view'];
  // No `permission` means the row rides the group's own rbac.view gate, which is
  // how Roles & permissions has always behaved. `!loaded` keeps the brief
  // pre-hydration window showing links rather than hiding the only route to
  // fixing a permission problem.
  const accessRows = showAccess
    ? PMS_ACCESS_SECTIONS.filter(
        (s) => !('permission' in s) || !loaded || permissions[s.permission],
      )
    : [];
  const candidates = [...accessRows, ...PMS_MASTER_SECTIONS].map((s) => ({
    id: s.to,
    label: s.label,
    to: s.to,
    active: isActive(s.to),
  }));

  return (
    <div className="py-2">
      <FavouritesSection candidates={candidates} onNavigate={onNavigate} />
      {/* Access — gated on rbac.view. The rail icon already uses ANY-of over
          masters+rbac, so the panel opens for either. */}
      {showAccess && (
        <div className="mt-4 first:mt-0">
          <PanelSectionHeader label="Access" />
          <div className="space-y-0.5 px-2">
            {accessRows.map((s) => (
              <HoverNavRow
                key={s.to}
                to={s.to}
                onNavigate={onNavigate}
                active={isActive(s.to)}
                favourite={{ id: s.to, label: s.label }}
              >
                <PanelRowIcon icon={s.icon} />
                <span className="flex-1 truncate">{s.label}</span>
              </HoverNavRow>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 first:mt-0">
        <PanelSectionHeader label="Master" />
        <div className="space-y-0.5 px-2">
          {PMS_MASTER_SECTIONS.map((s) => {
            const count = counts[s.to];
            return (
              <HoverNavRow
                key={s.to}
                to={s.to}
                onNavigate={onNavigate}
                active={isActive(s.to)}
                favourite={{ id: s.to, label: s.label }}
              >
                <PanelRowIcon icon={s.icon} />
                <span className="flex-1 truncate">{s.label}</span>
                {typeof count === 'number' && (
                  <span className="text-muted-foreground ml-auto flex-shrink-0 text-xs">
                    {count}
                  </span>
                )}
              </HoverNavRow>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** Body renderer passed to <AppShell renderPanelContent>. */
export function renderSidebarPanel(panelId: string, onNavigate: () => void): ReactNode {
  const body = renderPanelBody(panelId, onNavigate);
  return body ? (
    <PanelFavouritesProvider key={panelId} panelId={panelId}>
      {body}
    </PanelFavouritesProvider>
  ) : null;
}

function renderPanelBody(panelId: string, onNavigate: () => void): ReactNode {
  // Object-driven panels win: an app that declares `sidebar` in the registry
  // renders through the engine, with no component in this file. The switch
  // below is the remaining hand-written surface, app by app, until each app's
  // panels move into its own config.
  const declarative = getDeclarativePanel(panelId);
  if (declarative) {
    return (
      // Keyed by panel id so switching panels REMOUNTS the engine — its
      // useQueries array and provider hook order stay stable per panel.
      <DeclarativePanel
        key={panelId}
        config={declarative}
        onNavigate={onNavigate}
        providers={SIDEBAR_PROVIDERS}
        sectionProviders={SIDEBAR_SECTION_PROVIDERS}
      />
    );
  }
  switch (panelId) {
    case 'payroll-users':
      return <PayrollUsersPanel onNavigate={onNavigate} />;
    case 'payroll-pay-history':
      return <PayrollPayHistoryPanel onNavigate={onNavigate} />;
    case 'payroll-settings':
    case 'payroll-payouts':
    case 'payroll-compliance':
      return <PayrollPanel panelId={panelId} onNavigate={onNavigate} />;
    case 'pms-my-team':
      return <PmsMyTeamPanel onNavigate={onNavigate} />;
    case 'pms-forms':
      return <PmsFormsPanel onNavigate={onNavigate} />;
    case 'pms-users':
      return <PmsUsersPanel onNavigate={onNavigate} />;
    case 'pms-settings':
      return <PmsSettingsPanel onNavigate={onNavigate} />;
    default:
      return null;
  }
}
